import { useState, useCallback, useEffect } from 'react'
import { useAccount, useDisconnect, useSignMessage } from 'wagmi'

import { authService } from '../services/api'
import useLocalStorage from './useLocalStorage'

export default function useAuth() {
  const [userId, setUserId] = useLocalStorage<number | null>('user_id', null)
  const [token, setToken] = useLocalStorage<string | null>('jwt_token', null)
  const [apiKey, setApiKey] = useLocalStorage<string | null>('api_key', null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const { address } = useAccount()
  const { disconnectAsync } = useDisconnect()
  const { signMessageAsync } = useSignMessage()

  const signAndAuthenticate = useCallback(async () => {
    if (!address) {
      setError('Wallet not connected')
      return
    }

    setIsLoading(true)
    setError(null)

    try {
      const timestamp = Math.floor(Date.now() / 1000).toString()
      const message = timestamp.toString()

      // Sign the message
      const signature = await signMessageAsync({ message })

      // Send signature to backend for verification
      const response = await authService.authenticate(
        address,
        signature,
        timestamp
      )

      if (response.status === 'success' && response.data) {
        const { data } = response
        if(data.is_admin || data.is_whitelisted) {
          setUserId(data.user.id)
          setToken(data.token)
          setApiKey(data.user.api_key)
          return true
        } else {
          throw new Error('Your wallet is not whitelisted')
        }
      } else {
        throw new Error(response.message || 'Authentication failed')
      }
    } catch (err: any) {
      setError(err?.message || 'Failed to authenticate')
      return false
    } finally {
      setIsLoading(false)
    }
  }, [address, signMessageAsync, setUserId, setToken, setApiKey])

  const logout = useCallback(() => {
    disconnectAsync()
      .then(() => {
        setUserId(null)
        setToken(null)
        setApiKey(null)
        localStorage.removeItem('user_id')
        localStorage.removeItem('jwt_token')
        localStorage.removeItem('api_key')
      })
      .catch(console.error)
  }, [disconnectAsync, setUserId, setToken, setApiKey])

  const isAuthenticated = Boolean(userId && token)

  useEffect(() => {
    // If wallet is disconnected, clear auth
    if (!address && isAuthenticated) {
      logout()
    }
  }, [address, isAuthenticated, logout])

  return {
    userId,
    token,
    apiKey,
    isLoading,
    error,
    isAuthenticated,
    address,
    signAndAuthenticate,
    logout,
  }
}
